<script lang="ts" setup>
import { NuxtLink } from '#components'

const {
  user,
  workspace,
  isCurrentUserAdmin,
} = useAuth()

const {
  isMainSubscriptionNonRenewing,
  hasPaidSubscription,
  mainSubscription,
  hasValidSubscription,
  isMainSubscriptionFree,
} = useSubscription()

const workspaceModal = useTemplateRef<{ open: () => void }>('workspaceModal')
const isMenuOpen = ref(false)
const isContactUsModalOpen = ref(false)

const { t } = useI18n()

const {
  signatureBalance,
  freeContractBalance,
  hasUnlimitedFreeContracts,
  signatureSubscriptionBalance,
  hasUnlimitedSignatures,
} = useBalances()

const totalSignatureBalance = computed(() =>
  signatureBalance.value === -1 || signatureSubscriptionBalance.value === -1
    ? Number.POSITIVE_INFINITY
    : signatureBalance.value + signatureSubscriptionBalance.value,
)

const isSubscribeModalOpen = useSubscriptionModalOpenState()

const subscribeButtonAttributes = computed(() =>
  !hasValidSubscription.value || isMainSubscriptionFree.value
    ? {
        onClick: () => {
          isSubscribeModalOpen.value = true
        },
      }
    : { to: { name: '2-settings-order' } },
)

const { data: config } = useQueryConfig.current()

const canUsePrepaidSignatures = computed(() => !!config.value?.payment_methods?.onetime?.length)

const { hasFeature } = useFeatureAccess()

const isClientAccountManagementEnabled = computed(() => hasFeature('client-account-management'))

const referrerWorkspace = computed(() => workspace.value?.relationships?.referrerWorkspace?.data)
const hasFreeSubscription = computed(() => mainSubscription.value?.monthly_price === 0)

const referrerWorkspaceBannerText = computed(() => {
  if (!referrerWorkspace.value)
    return undefined

  return hasFreeSubscription.value
    ? t('home.referrerBanner.free', { name: referrerWorkspace.value?.attributes.name })
    : t('home.referrerBanner.paid', { name: referrerWorkspace.value?.attributes.name })
})

const closeMenu = () => {
  isMenuOpen.value = false
}
</script>

<template>
  <ChooseWorkspaceModal ref="workspaceModal" />
  <ContactUsModal
    v-if="isContactUsModalOpen"
    v-model="isContactUsModalOpen"
  />

  <UiDropdownMenu v-model:open="isMenuOpen">
    <UiDropdownMenuTrigger as-child>
      <button
        type="button"
        class="-ml-1.5 rounded-lg p-1.5 outline-none ring-gray-800 focus-visible:outline-none focus-visible:ring-2"
      >
        <div
          class="
            flex
            items-center
            justify-center
            gap-2.5
            rounded-lg
            text-left
            sm:min-w-[210px]
          "
        >
          <DTAvatar
            size="sm"
            variant="colored"
            :name="workspace?.attributes?.displayName ?? user?.attributes.name ?? ''"
            :src="workspace?.meta?.avatar?.url"
            :alt="workspace?.meta?.avatar?.name"
          />
          <div class="w-full">
            <p
              class="line-clamp-1 max-w-sm text-sm font-medium"
              :title="workspace?.attributes.displayName ?? ''"
            >
              {{ workspace?.attributes.displayName }}
            </p>
            <p
              class="line-clamp-1 text-3xs font-normal leading-[15px] text-gray-400"
              :title="user?.attributes.name"
            >
              {{ user?.attributes.name }}
            </p>
          </div>
          <div class="flex items-center justify-center">
            <span class="flex size-7 items-center justify-center rounded-full">
              <UiIcon
                name="docue:chevron-down"
                class="mt-0.5 size-4 text-gray-600"
              />
            </span>
          </div>
        </div>
      </button>
    </UiDropdownMenuTrigger>

    <UiDropdownMenuContent
      align="start"
      :side-offset="2"
      class="mb-1 w-[calc(100vw-2rem)] overflow-visible rounded-xl p-0 sm:w-max sm:min-w-[268px]"
    >
      <div class="flex flex-col divide-y divide-gray-200">
        <div class="flex flex-col gap-2 px-4.5 pb-2 pt-1.5">
          <div class="flex items-center justify-between gap-5">
            <div class="flex items-center gap-2">
              <span class="flex h-6 items-center">
                <UiIcon
                  v-if="hasPaidSubscription"
                  name="docue:solid-check-circle"
                  class="size-4"
                  :class="isMainSubscriptionNonRenewing ? 'text-yellow-500' : 'text-green-500'"
                />
                <UiIcon
                  v-else
                  name="docue:user-01"
                  class="size-4"
                />
              </span>
              <span class="line-clamp-1 max-w-[200px] break-all text-sm">
                {{
                  hasPaidSubscription
                    ? mainSubscription?.plan_name
                    : workspace?.attributes.displayName
                }}
              </span>
            </div>
            <DTButton
              v-bind="subscribeButtonAttributes"
              variant="link"
              dense
              size="xs"
              @click="closeMenu"
            >
              {{
                hasPaidSubscription
                  ? t('user-menu.subscription-details')
                  : t('user-menu.activate-subscription')
              }}
            </DTButton>
          </div>
          <div class="flex flex-col gap-1.5 pl-[22px]">
            <div
              v-if="!hasPaidSubscription || totalSignatureBalance > 0 || hasUnlimitedSignatures"
              class="flex items-center justify-between gap-1.5"
            >
              <span class="text-xs text-gray-600">
                {{ t('user-menu.signatures-remaining') }}
              </span>
              <span
                class="
                  rounded-md
                  border
                  border-gray-200
                  bg-gray-100
                  px-0.5
                  text-3xs
                  text-gray-600
                "
              >
                {{ Number.isFinite(totalSignatureBalance) ? totalSignatureBalance : t('unlimited') }}
              </span>
            </div>

            <PaymentButton
              v-if="canUsePrepaidSignatures && !hasValidSubscription && isCurrentUserAdmin"
              size="xs"
              variant="link"
              dense
              class="max-w-max"
              :products="[{ type: 'addon', id: 'signature-prepaid', quantity: 5 }]"
              :config="[{ id: 'signature-prepaid', min: 5, max: 100, description: t('product.signature.title') }]"
            >
              {{ t('user-menu.buy-more') }}
            </PaymentButton>

            <div class="flex items-center justify-between gap-1.5">
              <span class="text-xs text-gray-600">
                {{ t('user-menu.documents-remaining') }}
              </span>
              <span
                class="
                  rounded-md
                  border
                  border-gray-200
                  bg-gray-100
                  px-0.5
                  text-3xs
                  text-gray-600
                "
              >{{ hasUnlimitedFreeContracts ? t('unlimited') : freeContractBalance }}</span>
            </div>
          </div>
        </div>

        <div
          v-if="referrerWorkspace"
          class="flex max-w-xs gap-4 !border-t-0 bg-blue-100 px-4.5 py-3"
        >
          <p class="text-xs text-gray-800">
            {{ referrerWorkspaceBannerText }}
          </p>
          <DTAvatar
            v-if="referrerWorkspace?.meta?.avatar"
            size="sm"
            variant="colored"
            :name="referrerWorkspace?.attributes.name"
            :src="referrerWorkspace?.meta?.avatar?.url"
            :alt="referrerWorkspace?.meta?.avatar?.name"
          />
        </div>

        <div class="flex flex-col gap-0.5 p-1.5">
          <UiDropdownMenuItem
            :as="NuxtLink"
            to="/settings"
            icon-leading="docue:settings-02"
          >
            {{ t('user-menu.link.settings') }}
          </UiDropdownMenuItem>

          <UiDropdownMenuItem
            icon-leading="docue:switch-horizontal-01"
            @click="workspaceModal?.open()"
          >
            {{ t('user-menu.link.switch-workspaces') }}
          </UiDropdownMenuItem>

          <UiDropdownMenuItem
            :as="NuxtLink"
            to="/signup/create-workspace?allowBack=true"
            icon-leading="docue:plus"
          >
            {{ t('user-menu.link.new-workspace') }}
          </UiDropdownMenuItem>

          <UiDropdownMenuItem
            v-if="isClientAccountManagementEnabled"
            :as="NuxtLink"
            to="/2/clients"
            icon-leading="docue:briefcase-01"
          >
            {{ t('user-menu.link.clients') }}
          </UiDropdownMenuItem>

          <UiDropdownMenuSub>
            <UiDropdownMenuSubTrigger class="w-full gap-2">
              <UiIcon
                name="docue:life-buoy-01"
                class="size-4 shrink-0"
              />
              {{ t('user-menu.link.support') }}
            </UiDropdownMenuSubTrigger>
            <UiDropdownMenuSubContent>
              <AppSupportMenuItems @contact-us="isContactUsModalOpen = true" />
            </UiDropdownMenuSubContent>
          </UiDropdownMenuSub>
        </div>

        <div class="flex flex-col p-1.5">
          <span class="p-1.5 pl-2.5 text-xs text-gray-400">
            {{ user?.attributes.email }}
          </span>
          <UiDropdownMenuItem
            :as="NuxtLink"
            to="/2/logout"
            color="destructive"
            icon-leading="docue:log-out-01"
          >
            {{ t('auth.log-out') }}
          </UiDropdownMenuItem>
        </div>
      </div>
    </UiDropdownMenuContent>
  </UiDropdownMenu>
</template>
